/**
 * @module features/client-device-management/components/DeviceWifiDrawer
 * @description Slide-over inspector / mobile bottom sheet managing an assigned device's
 * single-active WiFi binding, in-place editing of active WiFi details, audited AES-256
 * password reveal with clipboard copy, vault-or-custom profile switching, and semantic
 * sticky-header Immutable WiFi Audit Trail table.
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  History,
  Loader2,
  Pencil,
  TriangleAlert,
  Unlink,
  Wifi,
  X,
} from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import {
  AssignmentSummary,
  CustomDeviceWifiFormSchema,
  CustomDeviceWifiFormValues,
  EditCustomDeviceWifiFormSchema,
  EditCustomDeviceWifiFormValues,
  WifiAuditAction,
} from "../types";
import {
  useAssignVaultWifi,
  useDeviceWifiAuditLogs,
  useDeviceWifiProfile,
  useOrgWifiVault,
  useRemoveDeviceWifi,
  useRevealDeviceWifiPassword,
  useSetCustomDeviceWifi,
  useUpdateDeviceWifiProfile,
} from "../api/wifiApi";

export interface DeviceWifiDrawerProps {
  assignment: AssignmentSummary | null;
  open: boolean;
  onClose: () => void;
}

function getAuditActionBadgeClasses(action: WifiAuditAction): string {
  switch (action) {
    case "assigned":
      return "bg-success-soft text-success border-success/20";
    case "switched":
    case "updated":
      return "bg-muted text-foreground border-border";
    case "removed":
      return "bg-warning-soft text-warning border-warning/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

export function DeviceWifiDrawer({
  assignment,
  open,
  onClose,
}: DeviceWifiDrawerProps) {
  const auditCaptionId = React.useId();
  const assignmentId = assignment?.id;
  const clientId = assignment?.client_id;
  const isActiveAssignment = assignment?.status === "active";

  const profileQuery = useDeviceWifiProfile(assignmentId, open);
  const vaultQuery = useOrgWifiVault(clientId, open);
  const auditQuery = useDeviceWifiAuditLogs(assignmentId, open);

  const assignVaultMutation = useAssignVaultWifi();
  const customWifiMutation = useSetCustomDeviceWifi();
  const updateWifiMutation = useUpdateDeviceWifiProfile();
  const removeWifiMutation = useRemoveDeviceWifi();
  const revealMutation = useRevealDeviceWifiPassword();

  const [mode, setMode] = React.useState<"vault" | "custom">("vault");
  const [selectedVaultId, setSelectedVaultId] = React.useState("");
  const [vaultBindNotes, setVaultBindNotes] = React.useState("");
  const [revealedPassword, setRevealedPassword] = React.useState<string | null>(
    null
  );
  const [copiedSecret, setCopiedSecret] = React.useState(false);
  const [showCustomInputPwd, setShowCustomInputPwd] = React.useState(false);
  const [actionError, setActionError] = React.useState<AppError | null>(null);
  const [statusNotice, setStatusNotice] = React.useState<string | null>(null);

  // In-place editing state for Current Active Boot Profile
  const [isEditingActive, setIsEditingActive] = React.useState(false);
  const [editActiveMode, setEditActiveMode] = React.useState<
    "vault" | "custom"
  >("custom");
  const [editVaultId, setEditVaultId] = React.useState("");
  const [editVaultNotes, setEditVaultNotes] = React.useState("");
  const [showEditCustomPwd, setShowEditCustomPwd] = React.useState(false);

  const customForm = useForm<CustomDeviceWifiFormValues>({
    resolver: zodResolver(CustomDeviceWifiFormSchema) as any,
    defaultValues: {
      custom_ssid_name: "",
      custom_password: "",
      notes: "",
    },
  });

  const editCustomForm = useForm<EditCustomDeviceWifiFormValues>({
    resolver: zodResolver(EditCustomDeviceWifiFormSchema) as any,
    defaultValues: {
      custom_ssid_name: "",
      custom_password: "",
      notes: "",
    },
  });

  // Immediately wipe any decrypted passphrase from React memory when drawer closes or assignment changes
  React.useEffect(() => {
    setRevealedPassword(null);
    setCopiedSecret(false);
    setActionError(null);
    setStatusNotice(null);
    setVaultBindNotes("");
    setIsEditingActive(false);
    setShowEditCustomPwd(false);
    customForm.reset();
    editCustomForm.reset();
  }, [assignmentId, open, customForm, editCustomForm]);

  const activeProfile = profileQuery.data;
  const vaultNetworks = React.useMemo(
    () => vaultQuery.data ?? [],
    [vaultQuery.data]
  );
  const auditLogs = auditQuery.data ?? [];

  // Pre-select active vault network or first vault network if available
  React.useEffect(() => {
    if (activeProfile?.client_wifi_network_id) {
      setSelectedVaultId(activeProfile.client_wifi_network_id);
    } else if (vaultNetworks.length > 0 && !selectedVaultId) {
      setSelectedVaultId(vaultNetworks[0].id);
    }
  }, [activeProfile?.client_wifi_network_id, vaultNetworks, selectedVaultId]);

  const handleClose = (): void => {
    setRevealedPassword(null);
    setCopiedSecret(false);
    setActionError(null);
    setStatusNotice(null);
    setIsEditingActive(false);
    onClose();
  };

  const handleStartEditActive = (): void => {
    if (!activeProfile) return;
    setActionError(null);
    setStatusNotice(null);
    setShowEditCustomPwd(false);

    if (activeProfile.client_wifi_network_id) {
      setEditActiveMode("vault");
      setEditVaultId(activeProfile.client_wifi_network_id);
      setEditVaultNotes(activeProfile.notes ?? "");
    } else {
      setEditActiveMode("custom");
      editCustomForm.reset({
        custom_ssid_name: activeProfile.ssid_name ?? "",
        custom_password: "",
        notes: activeProfile.notes ?? "",
      });
    }
    setIsEditingActive(true);
  };

  const handleSaveEditVaultActive = async (
    e: React.FormEvent
  ): Promise<void> => {
    e.preventDefault();
    if (!assignmentId || !editVaultId) return;
    setActionError(null);
    setStatusNotice(null);
    setRevealedPassword(null);
    try {
      const updated = await updateWifiMutation.mutateAsync({
        assignmentId,
        input: {
          client_wifi_network_id: editVaultId,
          notes: editVaultNotes.trim(),
        },
      });
      setIsEditingActive(false);
      setStatusNotice(`Updated active WiFi details for "${updated.ssid_name}".`);
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(rawErr, "Failed to update active WiFi details.")
      );
    }
  };

  const handleSaveEditCustomActive = async (
    values: EditCustomDeviceWifiFormValues
  ): Promise<void> => {
    if (!assignmentId) return;
    // If switching from a Vault-linked profile to a brand new Custom Override inside Edit,
    // require a 8-64 character passphrase since there is no existing custom_password_encrypted
    if (
      activeProfile?.client_wifi_network_id &&
      (!values.custom_password || values.custom_password.length < 8)
    ) {
      editCustomForm.setError("custom_password", {
        type: "manual",
        message:
          "Passphrase (8–64 characters) is required when converting a vault binding to a custom override.",
      });
      return;
    }

    setActionError(null);
    setStatusNotice(null);
    setRevealedPassword(null);
    try {
      const updated = await updateWifiMutation.mutateAsync({
        assignmentId,
        input: {
          custom_ssid_name: values.custom_ssid_name,
          custom_password: values.custom_password || undefined,
          notes: values.notes ?? "",
        },
      });
      setIsEditingActive(false);
      setShowEditCustomPwd(false);
      setStatusNotice(`Updated active WiFi details for "${updated.ssid_name}".`);
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(rawErr, "Failed to update active WiFi details.")
      );
    }
  };

  const handleToggleReveal = async (): Promise<void> => {
    if (!assignmentId) return;
    if (revealedPassword !== null) {
      setRevealedPassword(null);
      return;
    }
    setActionError(null);
    try {
      const plaintext = await revealMutation.mutateAsync(assignmentId);
      setRevealedPassword(plaintext);
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(rawErr, "Unable to decrypt WiFi passphrase.")
      );
    }
  };

  const handleCopyRevealed = async (): Promise<void> => {
    if (!revealedPassword) return;
    try {
      await navigator.clipboard.writeText(revealedPassword);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch {
      // Clipboard API may be blocked in insecure contexts; plaintext remains visible for manual copy.
      setActionError(
        new AppError({
          message:
            "Clipboard permission denied. Copy the revealed passphrase manually.",
        })
      );
    }
  };

  const handleBindVaultNetwork = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!assignmentId || !selectedVaultId) return;
    setActionError(null);
    setStatusNotice(null);
    setRevealedPassword(null);
    setIsEditingActive(false);
    try {
      const bound = await assignVaultMutation.mutateAsync({
        assignmentId,
        client_wifi_network_id: selectedVaultId,
        notes: vaultBindNotes.trim() || undefined,
      });
      setVaultBindNotes("");
      setStatusNotice(`Active WiFi switched to "${bound.ssid_name}".`);
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(rawErr, "Failed to bind vault WiFi profile.")
      );
    }
  };

  const handleCustomSubmit = async (
    values: CustomDeviceWifiFormValues
  ): Promise<void> => {
    if (!assignmentId) return;
    setActionError(null);
    setStatusNotice(null);
    setRevealedPassword(null);
    setIsEditingActive(false);
    try {
      const bound = await customWifiMutation.mutateAsync({
        assignmentId,
        input: values,
      });
      customForm.reset();
      setShowCustomInputPwd(false);
      setStatusNotice(`Configured custom WiFi override "${bound.ssid_name}".`);
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(
          rawErr,
          "Failed to configure custom WiFi override."
        )
      );
    }
  };

  const handleDetachWifi = async (): Promise<void> => {
    if (!assignmentId) return;
    setActionError(null);
    setStatusNotice(null);
    setRevealedPassword(null);
    setIsEditingActive(false);
    try {
      await removeWifiMutation.mutateAsync(assignmentId);
      setStatusNotice("Detached active WiFi profile from device.");
    } catch (rawErr: unknown) {
      setActionError(
        AppError.fromUnknown(rawErr, "Failed to detach WiFi profile.")
      );
    }
  };

  const deviceLabel = assignment
    ? assignment.custom_device_name || assignment.device_name
    : "Device";

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      className="sm:w-[640px] lg:w-[760px]"
      title={`${deviceLabel} — WiFi & Audit`}
      description={
        assignment
          ? `Single-active boot WiFi binding · ${assignment.registered_device_id}`
          : undefined
      }
    >
      <div className="space-y-5">
        {actionError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
          >
            <AlertCircle
              className="h-4 w-4 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p>{actionError.errorDetails}</p>
              {actionError.requestId && (
                <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                  Request ID: {actionError.requestId}
                </p>
              )}
            </div>
          </div>
        )}

        {statusNotice && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-soft p-3 text-xs text-success"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{statusNotice}</span>
          </div>
        )}

        {/* 1. Current Active WiFi Binding Card (With In-Place Editing) */}
        <section aria-label="Current active WiFi profile" className="space-y-2">
          <h3 className="text-xs font-semibold text-foreground">
            Current Active Boot Profile
          </h3>

          {profileQuery.isLoading ? (
            <Skeleton className="h-28 w-full rounded-xl" />
          ) : activeProfile ? (
            <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wifi
                      className="h-4 w-4 text-success shrink-0"
                      aria-hidden="true"
                    />
                    <span className="font-mono text-sm font-medium text-foreground truncate">
                      {activeProfile.ssid_name}
                    </span>
                    <span className="rounded-full bg-success-soft text-success border border-success/20 px-2 py-0.5 text-[10px] font-medium">
                      {activeProfile.network_name
                        ? "Vault Linked"
                        : "Custom Override"}
                    </span>
                  </div>
                  {activeProfile.network_name && (
                    <p className="text-xs text-muted-foreground">
                      Profile: {activeProfile.network_name}
                    </p>
                  )}
                  {activeProfile.notes && !isEditingActive && (
                    <p className="text-xs text-muted-foreground">
                      Notes: {activeProfile.notes}
                    </p>
                  )}
                </div>

                {isActiveAssignment && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!isEditingActive ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleStartEditActive}
                        aria-label="Edit active WiFi details"
                        className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1 bg-card"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>Edit</span>
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditingActive(false)}
                        className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>Cancel</span>
                      </Button>
                    )}

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={removeWifiMutation.isPending}
                      onClick={() => void handleDetachWifi()}
                      className="h-8 touch:min-h-[44px] px-2.5 text-xs text-danger hover:text-danger hover:bg-danger-soft gap-1"
                    >
                      {removeWifiMutation.isPending ? (
                        <Loader2
                          className="h-3.5 w-3.5 animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      <span>Detach</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Masked Passphrase with Audited Reveal & Copy */}
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border/70 bg-card px-3 py-2">
                <div className="min-w-0">
                  <span className="text-[11px] text-muted-foreground block">
                    WPA Passphrase
                  </span>
                  <span className="font-mono text-xs text-foreground break-all">
                    {revealedPassword !== null ? revealedPassword : "••••••••"}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={revealMutation.isPending}
                    onClick={() => void handleToggleReveal()}
                    aria-label={
                      revealedPassword !== null
                        ? "Hide WiFi passphrase"
                        : "Reveal WiFi passphrase"
                    }
                    className="h-8 w-8 touch:min-h-[44px] touch:min-w-[44px]"
                  >
                    {revealMutation.isPending ? (
                      <Loader2
                        className="h-3.5 w-3.5 animate-spin"
                        aria-hidden="true"
                      />
                    ) : revealedPassword !== null ? (
                      <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </Button>
                  {revealedPassword !== null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => void handleCopyRevealed()}
                      aria-label="Copy revealed passphrase"
                      className="h-8 w-8 touch:min-h-[44px] touch:min-w-[44px]"
                    >
                      {copiedSecret ? (
                        <Check
                          className="h-3.5 w-3.5 text-success"
                          aria-hidden="true"
                        />
                      ) : (
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Inline In-Place Edit Form for Current Active WiFi Details */}
              {isEditingActive && isActiveAssignment && (
                <div className="rounded-xl border border-border bg-card p-3.5 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-foreground">
                      Edit Active WiFi Details
                    </span>
                    {vaultNetworks.length > 0 && (
                      <div className="inline-flex rounded-full bg-muted p-0.5 text-xs">
                        <button
                          type="button"
                          onClick={() => {
                            setEditActiveMode("vault");
                            if (!editVaultId && vaultNetworks[0]) {
                              setEditVaultId(vaultNetworks[0].id);
                            }
                          }}
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                            editActiveMode === "vault"
                              ? "bg-card text-foreground shadow-card"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          Vault Binding
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditActiveMode("custom");
                            editCustomForm.reset({
                              custom_ssid_name: activeProfile.ssid_name ?? "",
                              custom_password: "",
                              notes: activeProfile.notes ?? "",
                            });
                          }}
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                            editActiveMode === "custom"
                              ? "bg-card text-foreground shadow-card"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          Custom SSID / Passphrase
                        </button>
                      </div>
                    )}
                  </div>

                  {editActiveMode === "vault" && vaultNetworks.length > 0 ? (
                    <form
                      onSubmit={handleSaveEditVaultActive}
                      className="space-y-3"
                    >
                      <fieldset className="space-y-3">
                        <legend className="sr-only">
                          Edit vault-linked WiFi binding
                        </legend>
                        <div className="space-y-1">
                          <label
                            id="edit-active-vault-wifi-label"
                            className="text-xs text-muted-foreground block"
                          >
                            Linked Vault Profile
                          </label>
                          <Select
                            value={editVaultId}
                            onValueChange={(val) => setEditVaultId(val)}
                          >
                            <SelectTrigger
                              aria-labelledby="edit-active-vault-wifi-label"
                              className="h-11 sm:h-9 bg-card rounded-xl"
                            >
                              <SelectValue placeholder="Choose vault network" />
                            </SelectTrigger>
                            <SelectContent>
                              {vaultNetworks.map((net) => (
                                <SelectItem key={net.id} value={net.id}>
                                  {net.network_name} ({net.ssid_name})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="edit-active-vault-notes"
                            className="text-xs text-muted-foreground block"
                          >
                            Binding Notes
                          </label>
                          <Input
                            id="edit-active-vault-notes"
                            maxLength={255}
                            value={editVaultNotes}
                            onChange={(e) => setEditVaultNotes(e.target.value)}
                            placeholder="e.g. Floor 2 East Wing AP"
                            className="h-11 sm:h-9 text-base sm:text-sm bg-card"
                          />
                        </div>
                      </fieldset>

                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingActive(false)}
                          className="h-9 touch:min-h-[44px] text-xs"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            !editVaultId || updateWifiMutation.isPending
                          }
                          className="h-9 touch:min-h-[44px] text-xs"
                        >
                          {updateWifiMutation.isPending ? (
                            <>
                              <Loader2
                                className="mr-2 h-3.5 w-3.5 animate-spin"
                                aria-hidden="true"
                              />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <span>Save WiFi Changes</span>
                          )}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <form
                      onSubmit={editCustomForm.handleSubmit(
                        handleSaveEditCustomActive
                      )}
                      className="space-y-3"
                      noValidate
                    >
                      <fieldset className="space-y-3">
                        <legend className="sr-only">
                          Edit custom WiFi SSID, passphrase, and notes
                        </legend>

                        <div className="space-y-1">
                          <label
                            htmlFor="edit-custom-wifi-ssid"
                            className="text-xs text-muted-foreground block"
                          >
                            WiFi SSID (1–32 chars){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="edit-custom-wifi-ssid"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            {...editCustomForm.register("custom_ssid_name")}
                            className="h-11 sm:h-9 text-base sm:text-sm font-mono bg-card"
                          />
                          {editCustomForm.formState.errors.custom_ssid_name && (
                            <p role="alert" className="text-[11px] text-danger">
                              {
                                editCustomForm.formState.errors.custom_ssid_name
                                  .message
                              }
                            </p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="edit-custom-wifi-pwd"
                            className="text-xs text-muted-foreground block"
                          >
                            New WPA Passphrase{" "}
                            {activeProfile.client_wifi_network_id ? (
                              <span className="text-danger">*</span>
                            ) : (
                              <span className="text-[11px] text-muted-foreground">
                                (Optional — leave blank to keep current)
                              </span>
                            )}
                          </label>
                          <div className="relative">
                            <Input
                              id="edit-custom-wifi-pwd"
                              type={showEditCustomPwd ? "text" : "password"}
                              autoComplete="new-password"
                              placeholder={
                                activeProfile.client_wifi_network_id
                                  ? "Enter 8–64 character passphrase"
                                  : "Leave blank to keep existing passphrase"
                              }
                              {...editCustomForm.register("custom_password")}
                              className="pr-10 h-11 sm:h-9 text-base sm:text-sm font-mono bg-card"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setShowEditCustomPwd((v) => !v)}
                              aria-label={
                                showEditCustomPwd
                                  ? "Hide edited passphrase"
                                  : "Show edited passphrase"
                              }
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                            >
                              {showEditCustomPwd ? (
                                <EyeOff
                                  className="h-3.5 w-3.5"
                                  aria-hidden="true"
                                />
                              ) : (
                                <Eye
                                  className="h-3.5 w-3.5"
                                  aria-hidden="true"
                                />
                              )}
                            </Button>
                          </div>
                          {editCustomForm.formState.errors.custom_password && (
                            <p role="alert" className="text-[11px] text-danger">
                              {
                                editCustomForm.formState.errors.custom_password
                                  .message
                              }
                            </p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="edit-custom-wifi-notes"
                            className="text-xs text-muted-foreground block"
                          >
                            WiFi Notes (Optional)
                          </label>
                          <Input
                            id="edit-custom-wifi-notes"
                            maxLength={255}
                            placeholder="e.g. Updated reception VLAN credentials"
                            {...editCustomForm.register("notes")}
                            className="h-11 sm:h-9 text-base sm:text-sm bg-card"
                          />
                          {editCustomForm.formState.errors.notes && (
                            <p role="alert" className="text-[11px] text-danger">
                              {editCustomForm.formState.errors.notes.message}
                            </p>
                          )}
                        </div>
                      </fieldset>

                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingActive(false)}
                          className="h-9 touch:min-h-[44px] text-xs"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={updateWifiMutation.isPending}
                          className="h-9 touch:min-h-[44px] text-xs"
                        >
                          {updateWifiMutation.isPending ? (
                            <>
                              <Loader2
                                className="mr-2 h-3.5 w-3.5 animate-spin"
                                aria-hidden="true"
                              />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <span>Save WiFi Changes</span>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-warning/30 bg-warning-soft p-3.5 flex items-start gap-2.5 text-xs">
              <TriangleAlert
                className="h-4 w-4 text-warning shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <p className="font-medium text-foreground">
                  No active WiFi profile configured
                </p>
                <p className="text-muted-foreground mt-0.5">
                  Attach a profile from the organization vault or set a custom
                  override so this hardware unit can connect on boot.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* 2. Attach / Switch WiFi Profile (Only on Active Assignments) */}
        {isActiveAssignment && !isEditingActive && (
          <section
            aria-label="Bind or switch WiFi profile"
            className="rounded-xl border border-border/80 bg-card p-4 space-y-3.5"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold text-foreground">
                {activeProfile
                  ? "Switch Active WiFi Profile"
                  : "Bind WiFi Profile"}
              </h3>
              <div className="inline-flex rounded-full bg-muted p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("vault")}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    mode === "vault"
                      ? "bg-card text-foreground shadow-card"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  From Vault
                </button>
                <button
                  type="button"
                  onClick={() => setMode("custom")}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    mode === "custom"
                      ? "bg-card text-foreground shadow-card"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Custom Override
                </button>
              </div>
            </div>

            {mode === "vault" ? (
              vaultNetworks.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">
                  No reusable profiles exist in this organization&apos;s WiFi
                  Vault yet. Add profiles in the{" "}
                  <strong>WiFi Network Vault</strong> tab or use{" "}
                  <strong>Custom Override</strong>.
                </p>
              ) : (
                <form onSubmit={handleBindVaultNetwork} className="space-y-3">
                  <fieldset className="space-y-3">
                    <legend className="sr-only">
                      Bind reusable organization vault WiFi profile
                    </legend>
                    <div className="space-y-1">
                      <label
                        id="select-vault-wifi-label"
                        className="text-xs text-muted-foreground block"
                      >
                        Organization Vault Profile
                      </label>
                      <Select
                        value={selectedVaultId}
                        onValueChange={(val) => setSelectedVaultId(val)}
                      >
                        <SelectTrigger
                          aria-labelledby="select-vault-wifi-label"
                          className="h-11 sm:h-10 bg-card rounded-xl"
                        >
                          <SelectValue placeholder="Choose vault network" />
                        </SelectTrigger>
                        <SelectContent>
                          {vaultNetworks.map((net) => (
                            <SelectItem key={net.id} value={net.id}>
                              {net.network_name} ({net.ssid_name})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label
                        htmlFor="vault-bind-notes"
                        className="text-xs text-muted-foreground block"
                      >
                        Binding Note (Optional)
                      </label>
                      <Input
                        id="vault-bind-notes"
                        maxLength={255}
                        value={vaultBindNotes}
                        onChange={(e) => setVaultBindNotes(e.target.value)}
                        placeholder="e.g. Moved to Floor 2 reception"
                        className="h-11 sm:h-9 text-base sm:text-sm bg-card"
                      />
                    </div>
                  </fieldset>

                  <Button
                    type="submit"
                    disabled={!selectedVaultId || assignVaultMutation.isPending}
                    className="w-full h-10 touch:min-h-[44px] text-xs"
                  >
                    {assignVaultMutation.isPending ? (
                      <>
                        <Loader2
                          className="mr-2 h-4 w-4 animate-spin"
                          aria-hidden="true"
                        />
                        <span>Applying Profile...</span>
                      </>
                    ) : (
                      <span>Set as Active Device WiFi</span>
                    )}
                  </Button>
                </form>
              )
            ) : (
              <form
                onSubmit={customForm.handleSubmit(handleCustomSubmit)}
                className="space-y-3"
                noValidate
              >
                <fieldset className="space-y-3">
                  <legend className="sr-only">
                    Configure custom device WiFi override
                  </legend>
                  <div className="space-y-1">
                    <label
                      htmlFor="custom-wifi-ssid"
                      className="text-xs text-muted-foreground block"
                    >
                      Custom SSID (1–32 chars){" "}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      id="custom-wifi-ssid"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      placeholder="e.g. Branch-Kiosk-Net"
                      {...customForm.register("custom_ssid_name")}
                      className="h-11 sm:h-9 text-base sm:text-sm font-mono bg-card"
                    />
                    {customForm.formState.errors.custom_ssid_name && (
                      <p role="alert" className="text-[11px] text-danger">
                        {customForm.formState.errors.custom_ssid_name.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="custom-wifi-pwd"
                      className="text-xs text-muted-foreground block"
                    >
                      WPA Passphrase (8–64 chars){" "}
                      <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="custom-wifi-pwd"
                        type={showCustomInputPwd ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Minimum 8 characters"
                        {...customForm.register("custom_password")}
                        className="pr-10 h-11 sm:h-9 text-base sm:text-sm font-mono bg-card"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowCustomInputPwd((v) => !v)}
                        aria-label={
                          showCustomInputPwd
                            ? "Hide passphrase"
                            : "Show passphrase"
                        }
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      >
                        {showCustomInputPwd ? (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                      </Button>
                    </div>
                    {customForm.formState.errors.custom_password && (
                      <p role="alert" className="text-[11px] text-danger">
                        {customForm.formState.errors.custom_password.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="custom-wifi-notes"
                      className="text-xs text-muted-foreground block"
                    >
                      Override Rationale (Optional)
                    </label>
                    <Input
                      id="custom-wifi-notes"
                      placeholder="e.g. Isolated guest kiosk subnet"
                      {...customForm.register("notes")}
                      className="h-11 sm:h-9 text-base sm:text-sm bg-card"
                    />
                  </div>
                </fieldset>

                <Button
                  type="submit"
                  disabled={customWifiMutation.isPending}
                  className="w-full h-10 touch:min-h-[44px] text-xs"
                >
                  {customWifiMutation.isPending ? (
                    <>
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                      <span>Encrypting &amp; Applying...</span>
                    </>
                  ) : (
                    <span>Save Custom WiFi Override</span>
                  )}
                </Button>
              </form>
            )}
          </section>
        )}

        {/* 3. Immutable WiFi Audit Trail (Semantic Sticky-Header Data Table) */}
        <section aria-label="WiFi transition audit trail" className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <History
                className="h-3.5 w-3.5 text-muted-foreground"
                aria-hidden="true"
              />
              <h3 className="text-xs font-semibold text-foreground">
                Immutable WiFi Audit Trail
              </h3>
            </div>
            {auditLogs.length > 0 && (
              <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground tabular-nums">
                {auditLogs.length} {auditLogs.length === 1 ? "event" : "events"}
              </span>
            )}
          </div>

          {auditQuery.isLoading ? (
            <div
              className="rounded-xl border border-border/80 bg-card overflow-hidden"
              aria-busy="true"
            >
              <div className="h-9 px-3 border-b border-border/80 flex items-center justify-between bg-muted/20">
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="divide-y divide-border/60">
                <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <Skeleton className="h-4 w-16 rounded-full" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-28" />
                </div>
                <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <Skeleton className="h-4 w-16 rounded-full" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-28" />
                </div>
              </div>
            </div>
          ) : auditQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger flex items-center justify-between gap-2"
            >
              <span>
                {AppError.fromUnknown(auditQuery.error).errorDetails ||
                  "Failed to load WiFi audit trail."}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void auditQuery.refetch()}
                className="h-7 bg-card text-foreground text-xs"
              >
                Retry
              </Button>
            </div>
          ) : auditLogs.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              No WiFi profile transitions recorded for this assignment yet.
            </p>
          ) : (
            <div className="@container w-full">
              <div
                tabIndex={0}
                role="region"
                aria-labelledby={auditCaptionId}
                className="max-h-[min(320px,45dvh)] overflow-auto rounded-xl border border-border/80 bg-card shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <table className="w-full border-separate border-spacing-0 text-xs tabular-nums">
                  <caption id={auditCaptionId} className="sr-only">
                    Immutable WiFi Audit Trail for {deviceLabel}
                  </caption>
                  <thead className="sticky top-0 z-sticky bg-card/95 backdrop-blur-sm">
                    <tr>
                      <th
                        scope="col"
                        className="sticky left-0 z-sticky bg-card/95 h-9 px-3 text-left align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80 whitespace-nowrap"
                      >
                        Action
                      </th>
                      <th
                        scope="col"
                        className="h-9 px-3 text-left align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80 whitespace-nowrap"
                      >
                        Previous SSID
                      </th>
                      <th
                        scope="col"
                        className="h-9 px-3 text-left align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80 whitespace-nowrap"
                      >
                        New SSID
                      </th>
                      <th
                        scope="col"
                        className="h-9 px-3 text-left align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80 whitespace-nowrap"
                      >
                        Changed By
                      </th>
                      <th
                        scope="col"
                        className="h-9 px-3 text-left align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80"
                      >
                        Reason / Notes
                      </th>
                      <th
                        scope="col"
                        className="h-9 px-3 text-right align-middle text-[11px] font-medium text-muted-foreground border-b border-border/80 whitespace-nowrap"
                      >
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr
                        key={log.id}
                        className="group transition-colors hover:bg-muted/40"
                      >
                        <th
                          scope="row"
                          className="sticky left-0 bg-card group-hover:bg-muted/40 transition-colors px-3 py-2.5 text-left align-middle font-normal border-b border-border/60 whitespace-nowrap"
                        >
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${getAuditActionBadgeClasses(
                              log.action
                            )}`}
                          >
                            {log.action}
                          </span>
                        </th>
                        <td className="px-3 py-2.5 align-middle font-mono text-[11px] text-muted-foreground border-b border-border/60 whitespace-nowrap">
                          {log.previous_ssid_name || "—"}
                        </td>
                        <td className="px-3 py-2.5 align-middle font-mono text-[11px] text-foreground border-b border-border/60 whitespace-nowrap">
                          {log.new_ssid_name || "Detached"}
                        </td>
                        <td className="px-3 py-2.5 align-middle text-[11px] text-foreground border-b border-border/60 whitespace-nowrap">
                          {log.changed_by_name}
                        </td>
                        <td
                          className="px-3 py-2.5 align-middle text-[11px] text-muted-foreground border-b border-border/60 max-w-[200px] truncate"
                          title={log.reason || undefined}
                        >
                          {log.reason || "—"}
                        </td>
                        <td className="px-3 py-2.5 align-middle text-right font-mono text-[10px] text-muted-foreground border-b border-border/60 whitespace-nowrap">
                          {new Date(log.changed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </Drawer>
  );
}
